import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { canWrite, hasPermission } from '../../rbac';
import { schemeAPI } from '../../services/api';
import useBackButtonClose from '../../hooks/useBackButtonClose';
import './Schemes.css';

const emptyForm = {
  name: '',
  short_name: '',
  category: 'housing',
  scheme_level: 'central',
  description: '',
  benefit_details: '',
  official_department: '',
  application_process: '',
  last_verified_at: '',
  eligibility_criteria: '',
  required_documents: [],
  benefit_amount: '',
  portal_url: '',
  start_date: '',
  end_date: '',
  is_active: true,
};

const categoryOptions = [
  ['housing', 'घरकुल'],
  ['financial', 'आर्थिक'],
  ['health', 'आरोग्य'],
  ['education', 'शिक्षण'],
  ['elderly', 'ज्येष्ठ नागरिक'],
  ['agriculture', 'कृषी'],
  ['women', 'महिला'],
  ['other', 'इतर'],
];

const levelOptions = [
  ['central', 'केंद्र'],
  ['state', 'राज्य'],
  ['district', 'जिल्हा'],
  ['local', 'ग्रामपंचायत'],
];

const statusOptions = [
  ['submitted', 'सबमिट'],
  ['under_review', 'तपासणी सुरू'],
  ['eligible', 'पात्र'],
  ['ineligible', 'अपात्र'],
  ['approved', 'मंजूर'],
  ['rejected', 'नाकारले'],
  ['disbursed', 'वितरित'],
  ['completed', 'पूर्ण'],
];

const formatDateInput = value => (value ? String(value).split('T')[0] : '');
const parseJsonValue = value => {
  let current = value;
  for (let i = 0; i < 4; i += 1) {
    if (typeof current !== 'string') return current;
    const trimmed = current.trim();
    if (!trimmed) return '';
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed === current) return current;
      current = parsed;
    } catch {
      return current;
    }
  }
  return current;
};
const cleanDisplayText = value => String(value || '')
  .trim()
  .replace(/^[[\]{}\s'"]+|[[\]{}\s'"]+$/g, '')
  .replace(/^"(.*)"$/, '$1')
  .trim();
const itemText = item => {
  const parsed = parseJsonValue(item);
  if (!parsed) return '';
  if (typeof parsed === 'string') {
    const text = cleanDisplayText(parsed);
    const fieldMatch = text.match(/"(?:question|name|document_name|label|title|text)"\s*:\s*"([^"]+)"/i);
    return fieldMatch?.[1] || text;
  }
  if (Array.isArray(parsed)) return parsed.map(itemText).filter(Boolean).join(', ');
  return parsed.question || parsed.name || parsed.document_name || parsed.label || parsed.title || parsed.text || parsed.description || '';
};
const parseStructuredList = value => {
  const parsed = parseJsonValue(value);
  if (!parsed) return [];
  if (Array.isArray(parsed)) {
    const joined = parsed.every(item => typeof item === 'string') ? parsed.join(',') : '';
    const parsedJoined = joined ? parseJsonValue(joined) : null;
    if (Array.isArray(parsedJoined)) return parseStructuredList(parsedJoined);
    return parsed.flatMap(item => parseStructuredList(item));
  }
  if (typeof parsed === 'object') {
    const isStructuredItem = ['question', 'name', 'document_name', 'label', 'title', 'text', 'description', 'is_required', 'required']
      .some(field => Object.prototype.hasOwnProperty.call(parsed, field));
    if (isStructuredItem) return [parsed];
    return Object.values(parsed).flatMap(item => parseStructuredList(item));
  }
  if (typeof parsed !== 'string') return [String(parsed)];
  const text = String(parsed || '').trim();
  if (!text) return [];
  const parsedAgain = parseJsonValue(text);
  if (typeof parsedAgain !== 'string' || parsedAgain !== text) return parseStructuredList(parsedAgain);
  return text.split(/\r?\n/).map(item => item.trim()).filter(Boolean);
};
const parseLines = value => parseStructuredList(value).map(itemText).filter(Boolean);
const normalizeDocumentRequirements = (value, keepEmpty = false) => {
  return parseStructuredList(value).map(item => {
    const parsed = parseJsonValue(item);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return {
        name: itemText(parsed),
        is_required: (parsed.is_required ?? parsed.required ?? true) !== false,
      };
    }
    return { name: itemText(parsed), is_required: true };
  }).filter(item => item.name || keepEmpty);
};
const apiErrorMessage = (err, fallback) => {
  const details = err.response?.data?.details;
  if (Array.isArray(details) && details.length) {
    return details.map(item => item.message || item.field).filter(Boolean).join(', ');
  }
  return err.response?.data?.error || err.message || fallback;
};
const stringifyLines = value => {
  if (!value) return '';
  return parseStructuredList(value).map(itemText).filter(Boolean).join('\n');
};
const optionLabel = (options, value) => options.find(([key]) => key === value)?.[1] || value || '-';

export default function Schemes() {
  const { tenant, user } = useAuth();
  const slug = tenant?.slug;
  const canWriteThis = canWrite(user?.role, 'schemes', user);
  const canCreateThis = hasPermission(user, 'schemes', 'create');
  const canUpdateThis = hasPermission(user, 'schemes', 'update');
  const canDeleteThis = hasPermission(user, 'schemes', 'delete');
  const [schemes, setSchemes] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [tab, setTab] = useState('schemes');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [schemeSearch, setSchemeSearch] = useState('');
  const [schemeLevelFilter, setSchemeLevelFilter] = useState('');
  const [appSearch, setAppSearch] = useState('');
  const [appStatusFilter, setAppStatusFilter] = useState('');
  const [formData, setFormData] = useState(emptyForm);
  const [sorts,setSorts]=useState({schemes:{key:'name',direction:'asc'},applications:{key:'createdAt',direction:'desc'}});
  const [pages,setPages]=useState({schemes:1,applications:1});
  const [pageSize,setPageSize]=useState(10);

  const closeForm = useCallback(() => {
    setShowForm(false);
    setEditItem(null);
    setFormData(emptyForm);
    setError('');
  }, []);

  useBackButtonClose(showForm, closeForm);

  const loadData = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const [sRes, aRes] = await Promise.all([
        schemeAPI.getAdminAll(slug).catch(() => schemeAPI.getAll(slug)),
        schemeAPI.getApplications(slug).catch(() => ({ data: { applications: [] } })),
      ]);
      setSchemes(sRes.data.schemes || []);
      setApplications(aRes.data.applications || []);
    } catch (err) {
      setError('डेटा लोड करण्यात अयशस्वी');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setSuccess('');
    setError('');
    setEditItem(null);
    setFormData(emptyForm);
    setShowForm(true);
    setTab('schemes');
  };

  const handleEdit = (scheme) => {
    setSuccess('');
    setError('');
    setEditItem(scheme);
    setFormData({
      ...emptyForm,
      name: scheme.name || '',
      short_name: scheme.short_name || '',
      category: scheme.category || 'housing',
      scheme_level: scheme.scheme_level || 'central',
      description: scheme.description || '',
      benefit_details: scheme.benefit_details || '',
      official_department: scheme.official_department || '',
      application_process: stringifyLines(scheme.application_process),
      last_verified_at: formatDateInput(scheme.last_verified_at),
      eligibility_criteria: stringifyLines(scheme.eligibility_criteria),
      required_documents: normalizeDocumentRequirements(scheme.required_documents),
      benefit_amount: scheme.benefit_amount || '',
      portal_url: scheme.portal_url || '',
      start_date: formatDateInput(scheme.start_date),
      end_date: formatDateInput(scheme.end_date),
      is_active: scheme.is_active !== false,
    });
    setShowForm(true);
    setTab('schemes');
  };

  const buildPayload = () => ({
    ...formData,
    name: formData.name.trim(),
    short_name: formData.short_name.trim() || null,
    benefit_amount: formData.benefit_amount.trim() || null,
    portal_url: formData.portal_url.trim() || null,
    description: formData.description.trim() || null,
    benefit_details: formData.benefit_details.trim() || null,
    official_department: formData.official_department.trim() || null,
    application_process: parseLines(formData.application_process),
    last_verified_at: formData.last_verified_at || null,
    eligibility_criteria: parseLines(formData.eligibility_criteria),
    required_documents: normalizeDocumentRequirements(formData.required_documents),
    start_date: formData.start_date || null,
    end_date: formData.end_date || null,
  });

  const validateForm = () => {
    if (!formData.name.trim()) return 'Scheme name is required.';
    if (formData.start_date && formData.end_date && formData.end_date < formData.start_date) {
      return 'Scheme end date cannot be before start date.';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      if (editItem) {
        await schemeAPI.update(slug, editItem.id, buildPayload());
        setSuccess('योजना अद्ययावत केली!');
      } else {
        await schemeAPI.create(slug, buildPayload());
        setSuccess('योजना तयार केली!');
      }
      closeForm();
      await loadData();
    } catch (err) {
      setError(apiErrorMessage(err, 'योजना जतन करण्यात अयशस्वी'));
    }
  };

  const handleDelete = async (scheme) => {
    if (!window.confirm(`"${scheme.name}" योजना हटवायची आहे का? अर्ज असल्यास योजना निष्क्रिय केली जाईल.`)) return;
    setError('');
    setSuccess('');
    try {
      const res = await schemeAPI.delete(slug, scheme.id);
      setSuccess(res.data?.message || 'योजना हटवली');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'योजना हटवण्यात अयशस्वी');
    }
  };

  const handleAppStatus = async (id, status) => {
    try {
      await schemeAPI.updateAppStatus(slug, id, { status });
      setSuccess(`अर्ज ${optionLabel(statusOptions, status)}`);
      loadData();
    } catch (err) {
      setError('अपडेट करण्यात अयशस्वी');
    }
  };

  const filteredSchemes = useMemo(() => schemes.filter(s =>
    (!schemeSearch || s.name?.toLowerCase().includes(schemeSearch.toLowerCase())) &&
    (!schemeLevelFilter || s.scheme_level === schemeLevelFilter)
  ).sort((a,b)=>{const sort=sorts.schemes,left=String(a[sort.key]??'').toLowerCase(),right=String(b[sort.key]??'').toLowerCase();return(left>right?1:left<right?-1:0)*(sort.direction==='asc'?1:-1);}),[schemes,schemeSearch,schemeLevelFilter,sorts.schemes]);

  const filteredApplications = useMemo(() => applications.filter(a => {
    const applicantName = a.applicant_name || a.applicant?.full_name || '';
    return (!appSearch ||
      applicantName.toLowerCase().includes(appSearch.toLowerCase()) ||
      a.Scheme?.name?.toLowerCase().includes(appSearch.toLowerCase())) &&
      (!appStatusFilter || a.status === appStatusFilter);
  }).sort((a,b)=>{const sort=sorts.applications;const value=item=>sort.key==='scheme'?item.Scheme?.name:sort.key==='applicant'?(item.applicant_name||item.applicant?.full_name):item[sort.key];const left=String(value(a)??'').toLowerCase(),right=String(value(b)??'').toLowerCase();return(left>right?1:left<right?-1:0)*(sort.direction==='asc'?1:-1);}),[applications,appSearch,appStatusFilter,sorts.applications]);
  const currentItems=tab==='schemes'?filteredSchemes:filteredApplications,totalPages=Math.max(1,Math.ceil(currentItems.length/pageSize)),currentPage=pages[tab],visibleItems=currentItems.slice((currentPage-1)*pageSize,currentPage*pageSize);
  useEffect(()=>{if(currentPage>totalPages)setPages(current=>({...current,[tab]:totalPages}));},[currentPage,totalPages,tab]);
  const changeSort=key=>{setSorts(current=>{const active=current[tab];return{...current,[tab]:{key,direction:active.key===key&&active.direction==='asc'?'desc':'asc'}};});setPages(current=>({...current,[tab]:1}));};
  const SortHeader=({column,children})=>{const sort=sorts[tab];return <button className={`scheme-sort-header ${sort.key===column?'active':''}`} onClick={()=>changeSort(column)}>{children}<span>{sort.key===column?(sort.direction==='asc'?'↑':'↓'):'↕'}</span></button>;};
  const exportCsv=()=>{const cell=value=>`"${String(value??'').replace(/"/g,'""')}"`;const rows=tab==='schemes'?[['Scheme','Category','Level','Benefit','Status'],...filteredSchemes.map(s=>[s.name,s.category,s.scheme_level,s.benefit_amount,s.is_active?'Active':'Inactive'])]:[['UID','Scheme','Applicant','Status','Date'],...filteredApplications.map(a=>[a.application_uid,a.Scheme?.name,a.applicant_name||a.applicant?.full_name,a.status,a.createdAt])];const link=document.createElement('a');link.href=URL.createObjectURL(new Blob(['\ufeff',rows.map(row=>row.map(cell).join(',')).join('\n')],{type:'text/csv;charset=utf-8'}));link.download=`${tab}-${new Date().toISOString().slice(0,10)}.csv`;link.click();URL.revokeObjectURL(link.href);};

  const setField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const addDocumentRequirement = () => {
    setFormData(prev => ({
      ...prev,
      required_documents: [
        ...normalizeDocumentRequirements(prev.required_documents, true),
        { name: '', is_required: true },
      ],
    }));
  };

  const updateDocumentRequirement = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      required_documents: normalizeDocumentRequirements(prev.required_documents, true).map((doc, docIndex) => (
        docIndex === index ? { ...doc, [field]: value } : doc
      )),
    }));
  };

  const removeDocumentRequirement = (index) => {
    setFormData(prev => ({
      ...prev,
      required_documents: normalizeDocumentRequirements(prev.required_documents, true).filter((_, docIndex) => docIndex !== index),
    }));
  };

  return (
    <div>
      <div className="admin-card-header">
        <h2>🎯 योजना</h2>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {!showForm && (
            <>
              <button className={`btn ${tab === 'schemes' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setTab('schemes')}>योजना</button>
              <button className={`btn ${tab === 'applications' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setTab('applications')}>अर्ज ({applications.length})</button>
            </>
          )}
          {tab === 'schemes' && (showForm ? canWriteThis : canCreateThis) && (
            <button className="btn btn-primary" onClick={showForm ? closeForm : openCreate}>
              {showForm ? 'रद्द करा' : '+ जोडा'}
            </button>
          )}
        </div>
      </div>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {showForm && tab === 'schemes' ? (
        <div className="admin-card">
          <div className="admin-form-title">
            <div>
              <span className="admin-form-eyebrow">{editItem ? 'संपादन' : 'नवीन नोंद'}</span>
              <h2>{editItem ? 'योजना संपादित करा' : 'नवीन योजना जोडा'}</h2>
            </div>
            <button type="button" className="btn btn-outline btn-sm" onClick={closeForm}>यादीकडे परत</button>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
              <div className="form-group"><label>नाव *</label><input value={formData.name} onChange={e => setField('name', e.target.value)} required /></div>
              <div className="form-group"><label>लघु नाव</label><input value={formData.short_name} onChange={e => setField('short_name', e.target.value)} placeholder="PMAY, PM-KISAN" /></div>
              <div className="form-group"><label>विभाग / प्रकार *</label><select value={formData.category} onChange={e => setField('category', e.target.value)} required>{categoryOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div>
              <div className="form-group"><label>स्तर</label><select value={formData.scheme_level} onChange={e => setField('scheme_level', e.target.value)}>{levelOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div>
              <div className="form-group"><label>लाभ रक्कम</label><input value={formData.benefit_amount} onChange={e => setField('benefit_amount', e.target.value)} placeholder="Rs 50,000 / Rs 6,000/year" /></div>
              <div className="form-group"><label>अधिकृत पोर्टल / अर्ज लिंक</label><input type="url" value={formData.portal_url} onChange={e => setField('portal_url', e.target.value)} placeholder="https://..." /></div>
              <div className="form-group"><label>अंमलबजावणी विभाग</label><input value={formData.official_department} onChange={e => setField('official_department', e.target.value)} placeholder="संबंधित शासकीय विभाग" /></div>
              <div className="form-group"><label>माहिती पडताळणी तारीख</label><input type="date" value={formData.last_verified_at} onChange={e => setField('last_verified_at', e.target.value)} /></div>
              <div className="form-group"><label>सुरुवात तारीख</label><input type="date" max={formData.end_date || undefined} value={formData.start_date} onChange={e => setField('start_date', e.target.value)} /></div>
              <div className="form-group"><label>शेवट तारीख</label><input type="date" min={formData.start_date || undefined} value={formData.end_date} onChange={e => setField('end_date', e.target.value)} /></div>
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label><input type="checkbox" checked={formData.is_active} onChange={e => setField('is_active', e.target.checked)} /> सक्रिय योजना</label>
              </div>
              <div className="form-group" style={{ gridColumn: 'span 2' }}><label>वर्णन</label><textarea rows="3" value={formData.description} onChange={e => setField('description', e.target.value)} /></div>
              <div className="form-group" style={{ gridColumn: 'span 2' }}><label>लाभ तपशील</label><textarea rows="2" value={formData.benefit_details} onChange={e => setField('benefit_details', e.target.value)} placeholder="योजनेत मिळणाऱ्या लाभाचा तपशील" /></div>
              <div className="form-group" style={{ gridColumn: 'span 2' }}><label>पात्रता निकष</label><textarea rows="3" value={formData.eligibility_criteria} onChange={e => setField('eligibility_criteria', e.target.value)} placeholder="प्रत्येक निकष नवीन ओळीत लिहा" /></div>
              <div className="form-group" style={{ gridColumn: 'span 2' }}><label>अर्ज प्रक्रिया</label><textarea rows="4" value={formData.application_process} onChange={e => setField('application_process', e.target.value)} placeholder="प्रत्येक अर्ज टप्पा नवीन ओळीत लिहा" /></div>
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label>आवश्यक कागदपत्रे</label>
                <div className="scheme-document-editor" style={{ display: 'grid', gap: 10 }}>
                  {normalizeDocumentRequirements(formData.required_documents, true).map((doc, index) => (
                    <div key={index} style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) auto auto', gap: 10, alignItems: 'center' }}>
                      <input value={doc.name} onChange={e => updateDocumentRequirement(index, 'name', e.target.value)} placeholder="कागदपत्राचे नाव" />
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, margin: 0 }}>
                        <input type="checkbox" checked={doc.is_required !== false} onChange={e => updateDocumentRequirement(index, 'is_required', e.target.checked)} />
                        Required
                      </label>
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => removeDocumentRequirement(index)}>Remove</button>
                    </div>
                  ))}
                  <button type="button" className="btn btn-outline btn-sm" style={{ justifySelf: 'start' }} onClick={addDocumentRequirement}>+ Add document</button>
                </div>
              </div>
            </div>
            <div className="admin-form-actions" style={{ marginTop: 15 }}>
              <button type="button" className="btn btn-outline" onClick={closeForm}>रद्द करा</button>
              <button type="submit" className="btn btn-primary">{editItem ? 'अद्ययावत करा' : 'योजना तयार करा'}</button>
            </div>
          </form>
        </div>
      ) : (
        <div className="admin-card scheme-table-card">
          {loading && currentItems.length === 0 ? <p>लोड होत आहे...</p> : tab === 'schemes' ? (
            <>
              <div className="scheme-table-toolbar">
                <label className="scheme-search"><span>⌕</span><input placeholder="योजनेच्या नावाने शोधा..." value={schemeSearch} onChange={e => {setSchemeSearch(e.target.value);setPages(p=>({...p,schemes:1}));}} /></label>
                <select value={schemeLevelFilter} onChange={e => {setSchemeLevelFilter(e.target.value);setPages(p=>({...p,schemes:1}));}}>
                  <option value="">सर्व स्तर</option>
                  {levelOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
                <button className="scheme-export-btn" onClick={exportCsv}>⇩ CSV निर्यात</button>
              </div>
              <div className="scheme-table-meta"><strong>{filteredSchemes.length}</strong> योजना नोंदी</div><div className="table-scroll scheme-table-scroll">
                <table className="data-table scheme-data-table">
                  <thead><tr><th><SortHeader column="name">नाव</SortHeader></th><th><SortHeader column="category">विभाग</SortHeader></th><th><SortHeader column="scheme_level">स्तर</SortHeader></th><th>लाभ</th><th>लिंक</th><th><SortHeader column="is_active">स्थिती</SortHeader></th><th>कृती</th></tr></thead>
                  <tbody>
                    {filteredSchemes.length === 0 ? (
                      <tr><td colSpan="7">योजना आढळल्या नाहीत.</td></tr>
                    ) : visibleItems.map((s) => (
                      <tr key={s.id}>
                        <td><div className="scheme-name-cell"><strong>{s.name}</strong><small>{s.short_name||s.description?.slice(0,60)||'तपशील उपलब्ध नाही'}</small></div></td>
                        <td><span className="scheme-category">{optionLabel(categoryOptions, s.category)}</span></td>
                        <td><span className="scheme-level">{optionLabel(levelOptions, s.scheme_level)}</span></td>
                        <td>{s.benefit_amount || '-'}</td>
                        <td>{s.portal_url ? <a href={s.portal_url} target="_blank" rel="noreferrer">पोर्टल</a> : '-'}</td>
                        <td><span className={`scheme-status ${s.is_active?'active':'inactive'}`}>{s.is_active ? 'सक्रिय' : 'निष्क्रिय'}</span></td>
                        <td>
                          {(canUpdateThis || canDeleteThis) && (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {canUpdateThis && <button className="btn btn-outline btn-sm" onClick={() => handleEdit(s)}>संपादित करा</button>}
                              {canDeleteThis && <button className="btn btn-danger btn-sm" onClick={() => handleDelete(s)}>हटवा</button>}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <SchemePagination page={currentPage} totalPages={totalPages} pageSize={pageSize} setPageSize={setPageSize} setPage={value=>setPages(p=>({...p,schemes:value}))}/>
            </>
          ) : (
            <>
              <div className="scheme-table-toolbar">
                <label className="scheme-search"><span>⌕</span><input placeholder="अर्जदार किंवा योजनेच्या नावाने शोधा..." value={appSearch} onChange={e => {setAppSearch(e.target.value);setPages(p=>({...p,applications:1}));}} /></label>
                <select value={appStatusFilter} onChange={e => {setAppStatusFilter(e.target.value);setPages(p=>({...p,applications:1}));}}>
                  <option value="">सर्व स्थिती</option>
                  {statusOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
                <button className="scheme-export-btn" onClick={exportCsv}>⇩ CSV निर्यात</button>
              </div>
              <div className="scheme-table-meta"><strong>{filteredApplications.length}</strong> अर्ज नोंदी</div><div className="table-scroll scheme-table-scroll">
                <table className="data-table scheme-data-table">
                  <thead><tr><th><SortHeader column="application_uid">UID</SortHeader></th><th><SortHeader column="scheme">योजना</SortHeader></th><th><SortHeader column="applicant">अर्जदार</SortHeader></th><th><SortHeader column="status">स्थिती</SortHeader></th><th><SortHeader column="createdAt">तारीख</SortHeader></th><th>कृती</th></tr></thead>
                  <tbody>
                    {filteredApplications.length === 0 ? (
                      <tr><td colSpan="6">अर्ज आढळले नाहीत.</td></tr>
                    ) : visibleItems.map((a) => (
                      <tr key={a.id}>
                        <td>{a.application_uid}</td>
                        <td>{a.Scheme?.name || '-'}</td>
                        <td>{a.applicant_name || a.applicant?.full_name || '-'}</td>
                        <td><span className={`badge badge-${a.status === 'approved' ? 'success' : a.status === 'rejected' ? 'danger' : 'warning'}`}>{optionLabel(statusOptions, a.status)}</span></td>
                        <td>{new Date(a.createdAt).toLocaleDateString('mr-IN')}</td>
                        <td>
                          {canUpdateThis && (
                            <div style={{ display: 'flex', gap: 5 }}>
                              <button className="btn btn-success btn-sm" onClick={() => handleAppStatus(a.id, 'approved')}>मंजूर</button>
                              <button className="btn btn-danger btn-sm" onClick={() => handleAppStatus(a.id, 'rejected')}>नकार</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <SchemePagination page={currentPage} totalPages={totalPages} pageSize={pageSize} setPageSize={setPageSize} setPage={value=>setPages(p=>({...p,applications:value}))}/>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SchemePagination({page,totalPages,pageSize,setPageSize,setPage}) {
  return <div className="table-pagination scheme-pagination"><label>प्रति पृष्ठ <select value={pageSize} onChange={e=>{setPageSize(Number(e.target.value));setPage(1);}}><option>10</option><option>20</option><option>50</option></select></label><button className="btn btn-outline btn-sm" disabled={page===1} onClick={()=>setPage(Math.max(1,page-1))}>← मागील</button><span>पृष्ठ <strong>{page}</strong> / {totalPages}</span><button className="btn btn-outline btn-sm" disabled={page>=totalPages} onClick={()=>setPage(Math.min(totalPages,page+1))}>पुढील →</button></div>;
}
